import type {Page} from '@PosterWhiteboard/page/page.class';
import type {Poster} from '@PosterWhiteboard/poster/poster.class';
import type {ItemObject} from '@PosterWhiteboard/items/item/item.types';
import type {VideoData} from '@Libraries/add-media-library';
import {getCoverCenteredPlacement} from '@Utils/math.util';
import {WhiteboardDomElement} from '@PosterWhiteboard/poster/whiteboard-dom.types';

/**
 * Deselects everything before the user adds or duplicates a page. The add/duplicate buttons are
 * DOM controls, so — unlike a canvas click — they never fire the pointer event that clears the
 * selection on the other pages (see PosterPages.initSelectionEvents). Without this, the previously
 * selected item's editing panel and its now-mis-rendered selection border linger onto the newly
 * added page.
 */
const clearSelectionForPageAdd = (): void => {
  window.posterEditor?.whiteboard?.pages.clearSelection();
};

// When no page is selected (indexOf === -1) append to the end — inserting at 0 would make addNewPage read pagesInOrder[-1].background and crash.
const getOrderForNewPageAfterCurrentPage = (poster: Poster): number => {
  const currentPageIndex = poster.pages.pageOrder.indexOf(poster.getCurrentPageId());
  return currentPageIndex === -1 ? poster.pages.pageOrder.length : currentPageIndex + 1;
};

export const addNewBlankPage = async (): Promise<void> => {
  const poster = window.posterEditor?.whiteboard;
  if (!poster) {
    return;
  }
  await addNewPage(getOrderForNewPageAfterCurrentPage(poster));
};

export const addNewPage = async (order?: number): Promise<void> => {
  if (!window.posterEditor?.whiteboard?.pages) {
    throw Error('No poster found to add page. Initialize a poster first');
  }

  const pagesInOrder = window.posterEditor.whiteboard.pages.getPagesInOrder();
  const pageOrder = order ?? pagesInOrder?.length;

  if (pagesInOrder.length >= window.PMW.MAX_NUMBER_OF_PAGES) {
    return;
  }

  clearSelectionForPageAdd();

  const sourcePage = pagesInOrder[pageOrder - 1];
  const sourceBgs = sourcePage?.items.getBackgrounds() ?? [];
  const itemsHashMap: Record<string, ItemObject> = {};
  const itemsOrder: string[] = [];
  // Multiple backgrounds means a timeline-sequenced background (e.g. video/slideshow); only carry
  // over a single static background onto the new page.
  if (sourceBgs.length === 1) {
    const cloned = sourceBgs[0].toObjectWithNewUids() as ItemObject;
    itemsHashMap[cloned.uid] = cloned;
    itemsOrder.push(cloned.uid);
  }
  const page = await window.posterEditor.whiteboard.pages.addPage({items: {itemsHashMap, itemsOrder}}, {pageOrder});
  scrollToPage(page.hashedID);
  page.poster.setCurrentPage(page.hashedID);
};

// The new page inherits the current page's background so transparent regions in the video fall back to the same backdrop.
export const addNewPageWithCoveringVideoItem = async (videoData: VideoData): Promise<string | undefined> => {
  const poster = window.posterEditor?.whiteboard;
  if (!poster?.pages) {
    throw Error('No poster found to add page. Initialize a poster first');
  }

  const pagesInOrder = poster.pages.getPagesInOrder();
  if (pagesInOrder.length >= window.PMW.MAX_NUMBER_OF_PAGES) {
    return undefined;
  }

  clearSelectionForPageAdd();

  const pageOrder = getOrderForNewPageAfterCurrentPage(poster);
  const sourceBgs = pagesInOrder[pageOrder - 1]?.items.getBackgrounds() ?? [];
  const itemsHashMap: Record<string, ItemObject> = {};
  const itemsOrder: string[] = [];
  if (sourceBgs.length === 1) {
    const cloned = sourceBgs[0].toObjectWithNewUids() as ItemObject;
    itemsHashMap[cloned.uid] = cloned;
    itemsOrder.push(cloned.uid);
  }
  const page = await poster.pages.addPage(
    {items: {itemsHashMap, itemsOrder}, zoom: poster.scaling.scale},
    {pageOrder, updateRedux: true, immediatelySelectOnceAdded: true}
  );

  await addCoveringVideoItemToPage(page, videoData);
  scrollToPage(page.hashedID);
  return page.hashedID;
};

const addCoveringVideoItemToPage = async (page: Page, videoData: VideoData): Promise<void> => {
  const {addItems} = page.items;
  if (!videoData.width || !videoData.height) {
    await addItems.addVideoItem(videoData);
    return;
  }

  const placement = getCoverCenteredPlacement(page.poster.width, page.poster.height, videoData.width, videoData.height);
  await addItems.addVideoItem({...videoData, ...placement});
};

export const goToPage = (pageId: string): void => {
  const poster = window.posterEditor?.whiteboard;
  if (!poster?.pages.getPage(pageId)) {
    return;
  }
  scrollToPage(pageId);
  poster.setCurrentPage(pageId);
};

/**
 * A page hidden by single-page mode has no layout position to scroll to; the toggle that reveals it
 * scrolls once the pages have their final size (PosterScaling.scrollToPendingPage).
 */
export const scrollToPage = (hashedId: string, animate = true): void => {
  const ANIMATION_DURATION = 300;
  const page = window.posterEditor?.whiteboard?.pages.getPage(hashedId);
  if (!page || page.isHidden()) {
    return;
  }
  const pageOffsetTop = page.pageContainer.offsetTop;
  const whiteboardContainer = window.posterEditor?.elements[WhiteboardDomElement.WHITEBOARD_CONTAINER];
  if (whiteboardContainer && typeof pageOffsetTop === 'number') {
    page.refreshFullCanvas();
    if (animate) {
      whiteboardContainer.animate({scrollTop: pageOffsetTop}, ANIMATION_DURATION, () => {
        window.posterEditor?.whiteboard?.refreshAllVisibleCanvases();
      });
    } else {
      whiteboardContainer.scrollTop(pageOffsetTop);
      window.posterEditor?.whiteboard?.refreshAllVisibleCanvases();
    }
  }
};

export const addDuplicatedPage = async (pageToDuplicate: Page): Promise<void> => {
  const poster = window.posterEditor?.whiteboard;
  const pages = poster?.pages;
  if (!pages || !poster) {
    throw Error('No poster found to add page. Initialize a poster first');
  }

  if (pages.pageOrder.length >= window.PMW.MAX_NUMBER_OF_PAGES) {
    return;
  }

  clearSelectionForPageAdd();

  // getDuplicatedPageObject reads live items only, so a lazily-loading page must finish first.
  await pageToDuplicate.loadDeferredPageContent();

  // A duplicated page reuses the same Getty assets (identical hashedFilenames), so it can never raise the unique Getty count — only unique assets count toward the limit, so no limit check is needed here.
  const duplicatedPageObject = pageToDuplicate.getDuplicatedPageObject();

  const pageOrder = (pages.getPageOrder(pageToDuplicate.hashedID) ?? 0) + 1;
  const page = await pages.addPage(duplicatedPageObject, {pageOrder, updateRedux: true, immediatelySelectOnceAdded: true});

  scrollToPage(page.hashedID);
};
